import { makeAutoObservable, runInAction } from "mobx"

export const ACTIVE_TURN_STATUSES = new Set(["queued", "waiting", "starting", "running"])

export interface NestTurnRuntimeUpdate {
    turnId: string
    sessionId?: string
    status?: string
    queuedAt?: string
    startedAt?: string
    completedAt?: string
    error?: string
}

export interface NestTaskRuntimeRecord {
    runtimeId: string
    taskId: string
    turnId: string
    sessionId?: string
    status: string
    startedAt: string
    updatedAt: string
    completedAt?: string
    error?: string
}

function isRuntimeActive(runtime: NestTaskRuntimeRecord): boolean {
    return ACTIVE_TURN_STATUSES.has(runtime.status)
}

function normalizedStatus(status: string | undefined): string | null {
    const normalized = status?.trim().toLowerCase()
    return normalized || null
}

function activityPriority(runtime: NestTaskRuntimeRecord): number {
    if (runtime.status === "running") return 3
    if (runtime.status === "starting" || runtime.status === "waiting") return 2
    return 1
}

function removedIds(before: Set<string>, after: Set<string>): string[] {
    return [...before].filter((taskId) => !after.has(taskId))
}

function timestampFor(update: NestTurnRuntimeUpdate): string {
    return update.completedAt || update.startedAt || update.queuedAt || new Date().toISOString()
}

export class RuntimeManager {
    runtimesById: Map<string, NestTaskRuntimeRecord> = new Map()
    private activeRuntimesByTaskId: Map<string, Map<string, NestTaskRuntimeRecord>> = new Map()
    private activeRuntimesBySessionId: Map<string, Map<string, NestTaskRuntimeRecord>> = new Map()
    private latestTerminalRuntimeBySessionId: Map<string, NestTaskRuntimeRecord> = new Map()
    hydrated = false

    constructor() {
        makeAutoObservable(this)
    }

    get runningTaskIds(): Set<string> {
        return new Set(this.activeRuntimesByTaskId.keys())
    }

    get hasRunningTasks(): boolean {
        return this.runningTaskIds.size > 0
    }

    isTaskRunning(taskId: string): boolean {
        return this.runningTaskIds.has(taskId)
    }

    isTaskOrphaned(_taskId: string): boolean {
        return false
    }

    activeRuntimeForTask(taskId: string): NestTaskRuntimeRecord | null {
        return (
            [...(this.activeRuntimesByTaskId.get(taskId)?.values() ?? [])].sort(
                (a, b) => activityPriority(b) - activityPriority(a) || b.updatedAt.localeCompare(a.updatedAt)
            )[0] ?? null
        )
    }

    activeTurnIdForTask(taskId: string): string | null {
        const runtime = [...(this.activeRuntimesByTaskId.get(taskId)?.values() ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]
        return runtime?.turnId ?? null
    }

    runtimeForSession(sessionId: string): NestTaskRuntimeRecord | null {
        const active = [...(this.activeRuntimesBySessionId.get(sessionId)?.values() ?? [])].sort(
            (left, right) => activityPriority(right) - activityPriority(left) || right.updatedAt.localeCompare(left.updatedAt)
        )[0]
        return active ?? this.latestTerminalRuntimeBySessionId.get(sessionId) ?? null
    }

    applyLiveTurnUpdate(taskId: string, update: NestTurnRuntimeUpdate): void {
        const previous = this.runtimesById.get(update.turnId)
        const runtime = runtimeRecordFromUpdate(taskId, update, previous)
        if (!runtime || !isRuntimeActive(runtime)) return

        runInAction(() => {
            this.replaceRuntime(runtime)
            this.hydrated = true
        })
    }

    applyTurnUpdate(taskId: string, update: NestTurnRuntimeUpdate): string[] {
        const before = this.runningTaskIds
        const previous = this.runtimesById.get(update.turnId)
        const nextStatus = normalizedStatus(update.status)
        if (previous && !isRuntimeActive(previous) && nextStatus && ACTIVE_TURN_STATUSES.has(nextStatus)) return []

        const runtime = runtimeRecordFromUpdate(taskId, update, previous)
        if (!runtime) return []

        runInAction(() => {
            this.replaceRuntime(runtime)
            this.hydrated = true
        })

        return removedIds(before, this.runningTaskIds)
    }

    reconcileLiveTurnIds(liveTurnIds: ReadonlySet<string>): NestTaskRuntimeRecord[] {
        const removed: NestTaskRuntimeRecord[] = []
        runInAction(() => {
            for (const [runtimeId, runtime] of this.runtimesById) {
                if (!isRuntimeActive(runtime) || liveTurnIds.has(runtime.turnId)) continue
                removed.push(runtime)
                this.runtimesById.delete(runtimeId)
                this.removeRuntimeIndexes(runtime)
            }
            this.hydrated = true
        })
        return removed
    }

    removeTask(taskId: string): void {
        runInAction(() => {
            for (const [runtimeId, runtime] of this.runtimesById) {
                if (runtime.taskId !== taskId) continue
                this.runtimesById.delete(runtimeId)
                this.removeRuntimeIndexes(runtime)
            }
        })
    }

    clear(): void {
        runInAction(() => {
            this.runtimesById.clear()
            this.activeRuntimesByTaskId.clear()
            this.activeRuntimesBySessionId.clear()
            this.latestTerminalRuntimeBySessionId.clear()
            this.hydrated = false
        })
    }

    private replaceRuntime(runtime: NestTaskRuntimeRecord): void {
        const previous = this.runtimesById.get(runtime.runtimeId)
        if (previous) this.removeRuntimeIndexes(previous)
        this.runtimesById.set(runtime.runtimeId, runtime)
        this.indexTaskRuntime(runtime)
        this.indexSessionRuntime(runtime)
    }

    private indexTaskRuntime(runtime: NestTaskRuntimeRecord): void {
        if (!isRuntimeActive(runtime)) return
        const active = this.activeRuntimesByTaskId.get(runtime.taskId) ?? new Map<string, NestTaskRuntimeRecord>()
        active.set(runtime.runtimeId, runtime)
        this.activeRuntimesByTaskId.set(runtime.taskId, active)
    }

    private removeRuntimeIndexes(runtime: NestTaskRuntimeRecord): void {
        const taskRuntimes = this.activeRuntimesByTaskId.get(runtime.taskId)
        taskRuntimes?.delete(runtime.runtimeId)
        if (taskRuntimes?.size === 0) this.activeRuntimesByTaskId.delete(runtime.taskId)
        this.removeSessionRuntime(runtime)
    }

    private removeSessionRuntime(runtime: NestTaskRuntimeRecord): void {
        const sessionId = runtime.sessionId
        if (!sessionId) return

        const active = this.activeRuntimesBySessionId.get(sessionId)
        active?.delete(runtime.runtimeId)
        if (active?.size === 0) this.activeRuntimesBySessionId.delete(sessionId)

        if (this.latestTerminalRuntimeBySessionId.get(sessionId)?.runtimeId === runtime.runtimeId) {
            this.latestTerminalRuntimeBySessionId.delete(sessionId)
        }
    }

    private indexSessionRuntime(runtime: NestTaskRuntimeRecord): void {
        const sessionId = runtime.sessionId
        if (!sessionId) return

        const active = this.activeRuntimesBySessionId.get(sessionId) ?? new Map<string, NestTaskRuntimeRecord>()
        if (isRuntimeActive(runtime)) {
            active.set(runtime.runtimeId, runtime)
            this.activeRuntimesBySessionId.set(sessionId, active)
            return
        }

        active.delete(runtime.runtimeId)
        if (active.size === 0) this.activeRuntimesBySessionId.delete(sessionId)

        const latestTerminal = this.latestTerminalRuntimeBySessionId.get(sessionId)
        if (!latestTerminal || latestTerminal.runtimeId === runtime.runtimeId || runtime.updatedAt >= latestTerminal.updatedAt) {
            this.latestTerminalRuntimeBySessionId.set(sessionId, runtime)
        }
    }
}

function runtimeRecordFromUpdate(taskId: string, update: NestTurnRuntimeUpdate, previous?: NestTaskRuntimeRecord): NestTaskRuntimeRecord | null {
    const status = normalizedStatus(update.status)
    if (!status) return null

    const updatedAt = timestampFor(update)
    const active = ACTIVE_TURN_STATUSES.has(status)
    const completedAt = update.completedAt ?? (!active ? previous?.completedAt : undefined)
    const error = update.error ?? (!active ? previous?.error : undefined)
    return {
        runtimeId: update.turnId,
        taskId,
        turnId: update.turnId,
        ...(update.sessionId || previous?.sessionId ? { sessionId: update.sessionId ?? previous?.sessionId } : {}),
        status,
        startedAt: update.startedAt || update.queuedAt || previous?.startedAt || updatedAt,
        updatedAt,
        ...(completedAt ? { completedAt } : {}),
        ...(error ? { error } : {}),
    }
}
